/**
 * The 41 Romanian counties plus the municipality of Bucharest — 42 administrative
 * units in total, which is what the form offers.
 *
 * `code` is the official SIRUTA/vehicle-registration county code. It is stored
 * rather than the display name so a CRM import keys on something stable, and so
 * a future locale can relabel without touching stored data.
 */

export interface Judet {
  code: string;
  name: string;
}

export const JUDETE: readonly Judet[] = [
  { code: 'AB', name: 'Alba' },
  { code: 'AR', name: 'Arad' },
  { code: 'AG', name: 'Argeș' },
  { code: 'BC', name: 'Bacău' },
  { code: 'BH', name: 'Bihor' },
  { code: 'BN', name: 'Bistrița-Năsăud' },
  { code: 'BT', name: 'Botoșani' },
  { code: 'BV', name: 'Brașov' },
  { code: 'BR', name: 'Brăila' },
  { code: 'B', name: 'București' },
  { code: 'BZ', name: 'Buzău' },
  { code: 'CS', name: 'Caraș-Severin' },
  { code: 'CL', name: 'Călărași' },
  { code: 'CJ', name: 'Cluj' },
  { code: 'CT', name: 'Constanța' },
  { code: 'CV', name: 'Covasna' },
  { code: 'DB', name: 'Dâmbovița' },
  { code: 'DJ', name: 'Dolj' },
  { code: 'GL', name: 'Galați' },
  { code: 'GR', name: 'Giurgiu' },
  { code: 'GJ', name: 'Gorj' },
  { code: 'HR', name: 'Harghita' },
  { code: 'HD', name: 'Hunedoara' },
  { code: 'IL', name: 'Ialomița' },
  { code: 'IS', name: 'Iași' },
  { code: 'IF', name: 'Ilfov' },
  { code: 'MM', name: 'Maramureș' },
  { code: 'MH', name: 'Mehedinți' },
  { code: 'MS', name: 'Mureș' },
  { code: 'NT', name: 'Neamț' },
  { code: 'OT', name: 'Olt' },
  { code: 'PH', name: 'Prahova' },
  { code: 'SM', name: 'Satu Mare' },
  { code: 'SJ', name: 'Sălaj' },
  { code: 'SB', name: 'Sibiu' },
  { code: 'SV', name: 'Suceava' },
  { code: 'TR', name: 'Teleorman' },
  { code: 'TM', name: 'Timiș' },
  { code: 'TL', name: 'Tulcea' },
  { code: 'VS', name: 'Vaslui' },
  { code: 'VL', name: 'Vâlcea' },
  { code: 'VN', name: 'Vrancea' },
] as const;

export const JUDET_CODES: readonly string[] = JUDETE.map((j) => j.code);

const BY_NAME = new Map(JUDETE.map((j) => [normalise(j.name), j.code]));

function normalise(value: string): string {
  return (
    value
      .normalize('NFD')
      // Strip combining marks (ă â î decompose into base + mark).
      .replace(/[\u0300-\u036f]/g, '')
      // ș and ț exist as precomposed comma-below and cedilla variants that do not
      // decompose to a plain s/t, so fold all four explicitly.
      .replace(/[şș]/gi, 's')
      .replace(/[ţț]/gi, 't')
      .toLowerCase()
      .trim()
  );
}

/**
 * Best-effort match of a Nominatim county string to a county code, so the form
 * can prefill the select after a reverse geocode. Nominatim returns values like
 * "Județul Ilfov" or "Municipiul București"; both should resolve.
 */
export function findJudetCode(raw: string | undefined | null): string {
  if (!raw) return '';
  const cleaned = normalise(raw)
    .replace(/^jude(t|tul)\s+/, '')
    .replace(/^municipiul\s+/, '')
    .replace(/^sector\s+\d+.*$/, 'bucuresti')
    .trim();

  const direct = BY_NAME.get(cleaned);
  if (direct) return direct;

  for (const [name, code] of BY_NAME) {
    if (cleaned.includes(name)) return code;
  }
  return '';
}

export function judetName(code: string): string {
  return JUDETE.find((j) => j.code === code)?.name ?? code;
}
